import { useSyncExternalStore } from "react";

/**
 * Home's moments (PRD §2.2.3, owner decision 2026-08-04). The addressed rooms
 * are routes; these are the states of `/` itself — and since onboarding became
 * the doorman conversation, there are only four: not yet known, nobody,
 * an identity that cannot own a door, and the chat.
 */
/** "doorman" is the shell — the phase kept its old name, and the shell is
 * still, at heart, the conversation with the doorman. */
export type HomePhase = "loading" | "signin" | "doorman";

/**
 * The little the SHELL knows that is neither an address nor server state.
 *
 * Two facts, and both are genuinely the chrome's business. The header's step
 * dots and the "Start this over" footer are both
 * statements about where the onboarding is, and the header is not inside it.
 * The notice is the sentence a screen leaves behind when it sends somebody
 * home — "That session expired." is said at `/`, by the sign-in screen, about
 * something that happened in another room.
 *
 * Deliberately in memory only. A reload is a fresh visit: it re-reads the
 * server, re-reads the draft, and starts wherever those two put it.
 */
type Shell = { phase: HomePhase; notice: string | null };

let state: Shell = { phase: "loading", notice: null };
const listeners = new Set<() => void>();

function emit(next: Shell): void {
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useShell(): Shell {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}

export function shellPhase(): HomePhase {
  return state.phase;
}

export function setShellPhase(phase: HomePhase): void {
  if (state.phase !== phase) emit({ ...state, phase });
}

export function setShellNotice(notice: string | null): void {
  if (state.notice !== notice) emit({ ...state, notice });
}

/** Test-only reset: the store outlives a `cleanup()` because it is not React's. */
export function resetShell(): void {
  emit({ phase: "loading", notice: null });
}
